import React, { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import axios from 'axios';
import API_URL from '../Config';
import Layout from './Layout/Layout';

/**
 * Live (scheduled pre-recorded) streams.
 *
 * - Asks the backend what is scheduled (GET /api/live/schedule, refreshed every 15 s).
 * - At the start time the backend (LiveStreamService) runs FFmpeg which pushes the video to
 *   MediaMTX. The stream whose status is LIVE is then played over HLS (stream.hlsUrl) with
 *   hls.js, so every viewer sees the same frame at the same time.
 * - UPCOMING streams show a countdown, ENDED streams are listed as finished.
 */

const REFRESH_MS = 15000;
const RETRY_MS = 3000;

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const formatCountdown = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d > 0 ? d + 'd ' : ''}${pad(h)}:${pad(m)}:${pad(sec)}`;
};

// Live rules:
//  - the viewer can go BACK (rewind) inside the HLS window MediaMTX keeps
//  - the viewer can NOT go FORWARD past the live edge (no skipping ahead)
//  - a viewer who comes late starts at the live edge and can rewind from there
const SEEK_TOLERANCE = 2; // seconds of slack past the live position
const GO_LIVE_THRESHOLD = 10; // show the "Go Live" button when this far behind

const getLiveEdge = (video) => {
  const r = video.seekable;
  return r && r.length ? r.end(r.length - 1) : null;
};

const LivePlayer = ({ stream }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [connecting, setConnecting] = useState(true);
  const [behindLive, setBehindLive] = useState(false);

  // Furthest point a viewer may reach = where "live" is for everybody else
  // (hls.js live sync position, a few segments behind the newest segment).
  // Viewers can rewind from here, but can never get ahead of it.
  const livePosition = () => {
    const video = videoRef.current;
    if (!video) return null;
    const edge = getLiveEdge(video);
    if (edge === null) return null;
    const hls = hlsRef.current;
    const sync = hls && hls.liveSyncPosition ? hls.liveSyncPosition : edge - 3;
    return Math.max(0, Math.min(edge, sync));
  };

  const goLive = () => {
    const video = videoRef.current;
    const pos = livePosition();
    if (video && pos !== null) {
      video.currentTime = pos;
      video.play().catch(() => {});
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return undefined;

    setConnecting(true);
    setBehindLive(false);
    let hls = null;
    let retryTimer = null;
    let destroyed = false;

    const onPlaying = () => setConnecting(false);

    // block seeking forward past the live edge (seek bar, arrow keys, anything)
    const onSeeking = () => {
      const max = livePosition();
      if (max !== null && video.currentTime > max + SEEK_TOLERANCE) {
        video.currentTime = max;
      }
    };

    // show / hide the "Go Live" button
    const onTimeUpdate = () => {
      const max = livePosition();
      setBehindLive(max !== null && max - video.currentTime > GO_LIVE_THRESHOLD);
    };

    video.addEventListener('playing', onPlaying);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('timeupdate', onTimeUpdate);

    if (Hls.isSupported()) {
      const create = () => {
        if (destroyed) return;
        hls = new Hls({
          // normal (non low-latency) HLS: every viewer is a few seconds behind the
          // real broadcast, but rewinding works properly
          lowLatencyMode: false,
          liveSyncDurationCount: 3,
          manifestLoadingMaxRetry: 6,
          manifestLoadingRetryDelay: 1500,
          levelLoadingMaxRetry: 6,
        });
        hlsRef.current = hls;
        hls.loadSource(stream.hlsUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // a viewer who comes late starts at the live edge (hls.js default for live)
          video.play().catch(() => {
            /* autoplay with sound can be blocked - the user presses play */
          });
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
          // NETWORK_ERROR is normal for the first seconds (FFmpeg is still starting
          // and MediaMTX has no playlist yet) -> rebuild the player and try again
          hls.destroy();
          hls = null;
          hlsRef.current = null;
          setConnecting(true);
          retryTimer = setTimeout(create, RETRY_MS);
        });
      };
      create();
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari plays HLS natively
      video.src = stream.hlsUrl;
      video.play().catch(() => {});
    }

    return () => {
      destroyed = true;
      clearTimeout(retryTimer);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('timeupdate', onTimeUpdate);
      if (hls) hls.destroy();
      hlsRef.current = null;
    };
    // restart the player only when a different stream goes live
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.id, stream.hlsUrl]);

  return (
    <div style={{ position: 'relative', background: '#000' }}>
      <video
        ref={videoRef}
        controls
        autoPlay
        playsInline
        controlsList="nodownload"
        style={{ width: '100%', aspectRatio: '16/9', display: 'block' }}
      />
      {behindLive && !connecting && (
        <button
          onClick={goLive}
          style={{
            position: 'absolute',
            left: '12px',
            bottom: '56px',
            background: '#e50914',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 12px',
            fontWeight: 700,
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          ● Go Live
        </button>
      )}
      {connecting && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            background: 'rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        >
          Connecting to live stream…
        </div>
      )}
    </div>
  );
};

const StreamCard = ({ s, countdown }) => (
  <div
    style={{
      width: '260px',
      background: '#1b1b1b',
      borderRadius: '10px',
      overflow: 'hidden',
      color: '#fff',
    }}
  >
    <img
      src={`${API_URL}/api/live/${s.id}/thumbnail`}
      alt={s.streamTitle}
      style={{ width: '100%', height: '146px', objectFit: 'cover', background: '#333' }}
      onError={(e) => { e.target.style.visibility = 'hidden'; }}
    />
    <div style={{ padding: '10px 12px' }}>
      <div style={{ fontWeight: 600 }}>{s.streamTitle}</div>
      <div style={{ fontSize: '12px', opacity: 0.75, marginTop: '4px' }}>
        {formatDateTime(s.startTime)}
      </div>
      {s.status === 'UPCOMING' && (
        <div style={{ fontSize: '13px', marginTop: '6px', color: '#ffc107' }}>
          Starts in {formatCountdown(countdown(s))}
        </div>
      )}
      {s.status === 'ENDED' && (
        <div style={{ fontSize: '13px', marginTop: '6px', opacity: 0.6 }}>Ended</div>
      )}
    </div>
  </div>
);

const PreRecordedLive = () => {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setTick] = useState(0); // re-render every second for the countdown
  const fetchedAt = useRef(Date.now());

  const loadSchedule = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/live/schedule`);
      fetchedAt.current = Date.now();
      setStreams(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      console.error('Error loading live schedule:', err);
      setError('Could not load the live schedule.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedule();
    const refresh = setInterval(loadSchedule, REFRESH_MS);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, [loadSchedule]);

  const secondsToStart = (s) =>
    s.secondsToStart - (Date.now() - fetchedAt.current) / 1000;

  // Status can change between two refreshes (countdown reached 0) – trust the clock too
  const liveNow = streams.find((s) => s.status === 'LIVE');
  const upcoming = streams.filter((s) => s.status === 'UPCOMING');
  const ended = streams.filter((s) => s.status === 'ENDED').reverse();

  // when a countdown hits zero, ask the server straight away
  useEffect(() => {
    if (upcoming.some((s) => secondsToStart(s) <= 0)) {
      loadSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  return (
    <Layout>
      <div style={{ padding: '90px 5% 40px', color: '#fff', minHeight: '80vh' }}>
        {loading && <p>Loading live schedule…</p>}
        {error && <p style={{ color: '#ff6b6b' }}>{error}</p>}

        {/* ON AIR */}
        {liveNow ? (
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span
                style={{
                  background: '#e50914',
                  borderRadius: '4px',
                  padding: '2px 10px',
                  fontWeight: 700,
                  fontSize: '13px',
                }}
              >
                ● LIVE
              </span>
              <h2 style={{ margin: 0 }}>{liveNow.streamTitle}</h2>
            </div>
            <LivePlayer stream={liveNow} />
            {liveNow.description && (
              <p style={{ marginTop: '14px', opacity: 0.85 }}>{liveNow.description}</p>
            )}
            {liveNow.tag && <p style={{ opacity: 0.6 }}>Tags: {liveNow.tag}</p>}
          </div>
        ) : (
          !loading &&
          !error && (
            <div
              style={{
                background: '#1b1b1b',
                borderRadius: '10px',
                padding: '40px',
                textAlign: 'center',
                marginBottom: '40px',
              }}
            >
              <h3 style={{ marginTop: 0 }}>Nothing is live right now</h3>
              <p style={{ opacity: 0.7, marginBottom: 0 }}>
                {upcoming.length > 0
                  ? `Next stream "${upcoming[0].streamTitle}" starts in ${formatCountdown(
                      secondsToStart(upcoming[0])
                    )}`
                  : 'No streams are scheduled yet.'}
              </p>
            </div>
          )
        )}

        {/* UPCOMING */}
        {upcoming.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h3>Upcoming</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px' }}>
              {upcoming.map((s) => (
                <StreamCard key={s.id} s={s} countdown={secondsToStart} />
              ))}
            </div>
          </div>
        )}

        {/* ENDED */}
        {ended.length > 0 && (
          <div>
            <h3>Earlier streams</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px' }}>
              {ended.map((s) => (
                <StreamCard key={s.id} s={s} countdown={secondsToStart} />
              ))}
            </div>
          </div>
        )}
        
      </div>
    </Layout>
  );
};

export default PreRecordedLive;